import { useEffect } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import {
  AugmentedUpdateClientOrganizationInput,
  ClientOrganizationKind,
} from '@/types/schema';

import { ClientOrganizationFormShape } from './ClientOrganizationModal.constants';
import { ClientOrganizationFragment } from './graphql/ClientOrganization.fragment.generated';
import { useGetClientOrganizationLazyQuery } from './graphql/ClientOrganizationModal.generated';

export function getFormValuesFromNode(
  data: ClientOrganizationFragment
): ClientOrganizationFormShape {
  const { address, ...restOrg } = data;

  return {
    kind: restOrg.kind || '',
    name: restOrg.name || '',
    address: {
      cityName: address?.cityName ?? '',
      stateCode: address?.stateCode ?? '',
    },
    pointOfContactEmail: restOrg.pointOfContactEmail || '',
    pointOfContactPhone: restOrg.pointOfContactPhone || '',
    pointOfContactName: restOrg.pointOfContactName || '',
    pointOfContactLastName: restOrg.pointOfContactLastName || '',
  };
}

export function mapFormDataToUpdateInput(
  formValues: ClientOrganizationFormShape,
  organizationId: string,
  householdId: string
): AugmentedUpdateClientOrganizationInput {
  const { address, ...clientOrganization } = formValues;

  return {
    id: organizationId,
    update: {
      name: clientOrganization.name,
      kind: clientOrganization.kind || ClientOrganizationKind.Other,
      ...(clientOrganization.pointOfContactEmail
        ? { pointOfContactEmail: clientOrganization.pointOfContactEmail }
        : { clearPointOfContactEmail: true }),
      ...(clientOrganization.pointOfContactPhone
        ? { pointOfContactPhone: clientOrganization.pointOfContactPhone }
        : { clearPointOfContactPhone: true }),
      ...(clientOrganization.pointOfContactName
        ? { pointOfContactName: clientOrganization.pointOfContactName }
        : { clearPointOfContactName: true }),
      ...(clientOrganization.pointOfContactLastName
        ? { pointOfContactLastName: clientOrganization.pointOfContactLastName }
        : { clearPointOfContactLastName: true }),
      clearAddress: true,
    },
    ...(address.cityName && address.stateCode
      ? { withAddress: { create: { ...address, householdID: householdId } } }
      : {}),
  };
}

export function useSyncClientOrganizationDataToForm(
  reset: UseFormReset<ClientOrganizationFormShape>,
  organizationId: string,
  onNotFound: () => void
) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { recoverExpectedNotFound, recoverMissingRecord } =
    useRecordNotFoundHandler({
      message: 'This organization is no longer available.',
      onNotFound,
    });
  const [getClientOrganization, queryProps] = useGetClientOrganizationLazyQuery(
    {
      variables: { id: organizationId },
      onError: (err) => {
        if (recoverExpectedNotFound(err)) {
          return;
        }

        showFeedback(
          "We weren't able to get the organization information. Please refresh the page and try again."
        );
        reportError(
          'EditClientOrganizationModal: Could not get organization',
          err,
          {
            organizationId,
          }
        );
      },
    }
  );

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    async function syncDataToForm() {
      const { data } = await getClientOrganization();
      const organization = data?.organization as ClientOrganizationFragment;
      if (organization) {
        reset(getFormValuesFromNode(organization));
      } else if (data) {
        recoverMissingRecord();
      }
    }

    void syncDataToForm();
  }, [reset, getClientOrganization, organizationId, recoverMissingRecord]);

  return queryProps;
}
